import { motion } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  Mail,
  Send,
  CheckCircle2,
  Layers,
  Inbox,
  TrendingUp,
  Users,
  Eye,
  MailCheck,
  ArrowUpRight,
} from "lucide-react";

import Seo from "../../Components/SEO/Seo";
import Container from "../../Components/Container/Container";
import Button from "../../Components/Button/Button";
import {
  EMAIL_HERO_STATS,
  EMAIL_BENEFITS,
  MULTICHANNEL_PILLARS,
} from "../../Data/emailMarketingData";

import "../../Style/services/data-validation.css";
import "../../Style/services/b2b-appointment.css";
import "../../Style/services/email-marketing.css";

const cardHover = {
  initial: { y: 0, opacity: 0.95 },
  hover: { y: -6, opacity: 1, transition: { duration: 0.25, ease: "easeOut" } },
};

export default function EmailMarketing() {
  const handleConsultation = () => {
    window.history.pushState({}, "", "/contact");
    window.dispatchEvent(new Event("popstate"));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="validation-page email-mkt-page">
      <Seo
        title="B2B Email Marketing Services | Conectify Tech"
        description="We don't just go to the inflatables, we bring up the people! Drive authentic messaging, direct buyer engagement, and 138% higher spending power."
      />

      {/* Hero Section */}
      <section className="val-hero email-hero">
        <Container className="val-hero__container">
          <motion.div
            className="val-hero__content"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
          >
            <div className="section-eyebrow">
              <Sparkles size={14} />
              <span>High-Converting Outreach</span>
            </div>

            <h1 className="val-hero__title">
              We Don't Just Go to The Inflatables,{" "}
              <span className="text-gradient">We Bring Up the People!</span>
            </h1>

            <p className="val-hero__lead">
              We offer exceptional email marketing services that will help you emphasize and
              therefore bond with your clients' beliefs and values.
            </p>

            <div className="val-hero__tags">
              <span className="val-tag">
                <Mail size={13} className="text-primary" />
                <span>99% Daily Inbox Check</span>
              </span>
              <span className="val-tag">
                <TrendingUp size={13} className="text-primary" />
                <span>Top Digital ROI Channel</span>
              </span>
              <span className="val-tag">
                <CheckCircle2 size={13} className="text-success" />
                <span>138% Higher Buyer Spend</span>
              </span>
            </div>

            <div className="val-hero__actions">
              <Button icon={<ArrowRight size={18} />} onClick={handleConsultation}>
                Launch An Email Campaign
              </Button>
              <Button variant="secondary" onClick={handleConsultation}>
                Explore Benefits
              </Button>
            </div>
          </motion.div>

          {/* Interactive Stat Stream Grid */}
          <motion.div
            className="val-hero__card-wrap"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.55, delay: 0.15 }}
          >
            <div className="email-stats-board">
              <div className="email-stats-board__header">
                <div className="flex items-center gap-2">
                  <Inbox size={18} className="text-primary" />
                  <span className="font-bold text-sm">Industry Email Telemetry</span>
                </div>
                <span className="hud-live-tag">
                  <span className="live-dot"></span> Live Benchmarks
                </span>
              </div>

              <div className="email-stats-grid">
                {EMAIL_HERO_STATS.map((stat, idx) => {
                  const Icon = stat.icon;
                  return (
                    <motion.div
                      key={idx}
                      className="email-stat-cell"
                      whileHover={{ scale: 1.03 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="email-stat-cell__top">
                        <span className="stat-pct">{stat.percentage}</span>
                        <div className="stat-icon">
                          <Icon size={14} />
                        </div>
                      </div>
                      <p className="stat-desc">{stat.desc}</p>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Authentic & Honest Messaging Section */}
      <section className="email-messaging-section">
        <Container>
          <div className="email-dispatch-shell">
            {/* Left Content Column */}
            <div className="email-dispatch-content">
              <div className="section-eyebrow">
                <Sparkles size={14} />
                <span>Direct Relationship Building</span>
              </div>

              <h2 className="email-dispatch-title">
                IT’S ALL ABOUT AUTHENTIC & <span className="text-gradient">HONEST MESSAGING</span>
              </h2>

              <p className="email-dispatch-lead">
                At Conectify Tech, we provide email marketing services to help you connect with your
                prospects and retain them directly. We create profitable marketing solutions aimed at
                ensuring that your company takes full advantage of marketing by increasing your
                company's online visibility.
              </p>

              <p className="email-dispatch-desc">
                Email marketing isn't just about achieving the goal of sending emails to your
                subscribers. It's about getting in touch with potential customers or customers and
                building a trusting relationship with them.
              </p>

              <p className="email-dispatch-desc">
                Email marketing is the most active form of direct marketing that plays an important
                role in building people's brand awareness in a way that they will remember. Our email
                marketing services will help you attract a large customer base in the most effective
                way! You know, people who contact each other via email spend 138% more than those who
                contact each other via other means? Our email marketing solutions help you create
                personalized messages for specific customers.
              </p>

              <div className="email-dispatch-highlights">
                <div className="dispatch-highlight-item">
                  <span className="num text-primary">+138%</span>
                  <span className="lbl">Higher Buyer Spend Over Alternative Channels</span>
                </div>
                <div className="dispatch-highlight-item">
                  <span className="num text-success">1-on-1</span>
                  <span className="lbl">Dynamic Personalization per Demographic</span>
                </div>
              </div>
            </div>

            {/* Right Interactive Outreach Console */}
            <div className="email-dispatch-console">
              <div className="console-ambient-glow"></div>

              {/* Floating Ticker Badge */}
              <div className="console-floating-pill">
                <TrendingUp size={14} className="text-primary" />
                <span>+138% Transaction Surge Active</span>
              </div>

              {/* Central Dispatch Card */}
              <div className="console-card">
                <div className="console-card__top">
                  <div className="console-card__status">
                    <span className="pulse-dot"></span>
                    <span>Campaign Broadcast Ready</span>
                  </div>
                  <span className="console-badge">1-to-1 Mode</span>
                </div>

                <div className="console-card__editor">
                  <div className="editor-row">
                    <span className="tag">To:</span>
                    <div className="recipients-stack">
                      <span className="chip">Target Buyers (Verified)</span>
                      <span className="chip chip--accent">+50K Records</span>
                    </div>
                  </div>
                  <div className="editor-row">
                    <span className="tag">Intent:</span>
                    <span className="editor-intent">High Commercial Purchase Propensity</span>
                  </div>
                  <div className="editor-preview-bubble">
                    <div className="bubble-header">
                      <MailCheck size={14} className="text-primary" />
                      <span>Hyper-Personalized Value Proposal</span>
                    </div>
                    <p>“Direct messaging aligned with your current tech stack evaluation...”</p>
                  </div>
                </div>

                {/* Performance Telemetry Strip */}
                <div className="console-card__metrics">
                  <div className="metric">
                    <div className="metric-head">
                      <Eye size={13} className="text-primary" />
                      <span>Open Target</span>
                    </div>
                    <span className="metric-value">68.4%</span>
                  </div>
                  <div className="metric">
                    <div className="metric-head">
                      <Users size={13} className="text-success" />
                      <span>Audience</span>
                    </div>
                    <span className="metric-value">50M+ Reach</span>
                  </div>
                  <div className="metric">
                    <div className="metric-head">
                      <ArrowUpRight size={13} className="text-cyan-500" />
                      <span>Spend Lift</span>
                    </div>
                    <span className="metric-value text-primary">+138%</span>
                  </div>
                </div>

                <div className="console-card__action">
                  <div className="console-action-btn">
                    <Send size={14} />
                    <span>Authentic Broadcast Engaged</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Benefits Of Email Marketing Services */}
      <section className="email-benefits-section">
        <Container>
          <div className="section-head text-center">
            <span className="section-eyebrow">BENEFITS OF EMAIL MARKETING SERVICES</span>
            <h2 className="section-title">Moving Ahead in The Journey of Marketing’</h2>
            <p className="section-desc max-w-2xl mx-auto">
              Empower your enterprise with scalable, personalized campaigns tailored for tangible
              revenue outcomes and durable client loyalty.
            </p>
          </div>

          <div className="val-why__grid email-benefits-grid">
            {EMAIL_BENEFITS.map((benefit) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={benefit.title}
                  className="val-why-card"
                  variants={cardHover}
                  initial="initial"
                  whileHover="hover"
                >
                  <div className="val-why-card__icon">
                    <Icon size={22} />
                  </div>
                  <h4>• {benefit.title}</h4>
                  <p>{benefit.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* Important Part of Multichannel Marketing */}
      <section className="email-multichannel-section">
        <Container>
          <div className="appt-split-box">
            <div className="email-multichannel-visual">
              <div className="cadence-orbit">
                <div className="cadence-orbit__ring"></div>
                <div className="cadence-center-node">
                  <Layers size={28} className="text-primary" />
                  <span>Multichannel Core</span>
                </div>

                <div className="orbit-node orbit-node--top">
                  <span>Print Media</span>
                </div>
                <div className="orbit-node orbit-node--right">
                  <span>Email Outreach</span>
                </div>
                <div className="orbit-node orbit-node--bottom">
                  <span>Online Traffic</span>
                </div>
                <div className="orbit-node orbit-node--left">
                  <span>Sales Velocity</span>
                </div>
              </div>
            </div>

            <div className="appt-split-box__content">
              <div className="section-eyebrow">Unified Brand Synergy</div>
              <h2>Important Part of Multichannel Marketing</h2>
              <p className="lead-text">
                Email marketing is part of multichannel marketing where we take a highly integrated
                approach to develop a strategy that includes print media marketing and a strong online
                presence. This increases your visibility and therefore online traffic and sales. We
                do our best to offer the best email marketing services for the best results.
              </p>
              <p className="lead-text">
                Today, multichannel marketing plays the most important role because to reach your
                target customers you need to adapt to the channel they have chosen. To achieve this,
                we are looking for a comprehensive approach to develop our comprehensive email
                marketing services that add value to your online presence.
              </p>

              <div className="appt-strategies-grid mt-4">
                {MULTICHANNEL_PILLARS.map((pillar) => (
                  <div key={pillar.title} className="appt-strategy-chip">
                    <CheckCircle2 size={16} className="text-primary" />
                    <span>{pillar.title}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Bottom CTA */}
      <section className="b2b-cta">
        <Container>
          <div className="b2b-cta__card">
            <h2>Accelerate Your Pipeline with Strategic Email Campaigns</h2>
            <p>
              Build lasting buyer relationships and achieve predictable revenue with Conectify
              Tech's multichannel email marketing solutions.
            </p>
            <Button icon={<ArrowRight size={18} />} onClick={handleConsultation}>
              Launch Your Email Strategy
            </Button>
          </div>
        </Container>
      </section>
    </div>
  );
}